<script setup lang="ts">
import { lectureService } from '@/api/services/lecture-service'
import { mentorService } from '@/api/services/mentor-service'
import BaseHorizontalList from '@/components/base/BaseHorizontalList.vue'
import BaseLayout from '@/components/base/BaseLayout.vue'
import BaseVideoThumbnail from '@/components/base/BaseVideoThumbnail.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { Lecture } from '@/types/lecture.types'
import type { MentorDetail } from '@/types/mentor.types'
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const id = Number(route.params.id)

const mentor = ref<MentorDetail>()

const isMobile = useIsMobile()

const videoList = ref<Lecture[]>([])

onMounted(async () => {
  const mentorDetail = await mentorService.getMentorDetail(id)
  mentor.value = mentorDetail

  const lectureList = await lectureService.getLectures({
    mentorIdx: id,
    size: 6,
  })
  videoList.value = lectureList.data
})

const handleVideoClick = (idx: number) => {
  router.push(`/video/${idx}`)
}
</script>

<template>
  <BaseLayout>
    <div class="mentor-detail-view">
      <div class="mentor-detail-view-left">
        <div class="mentor-image">
          <img :src="mentor?.portraitThumbnailUrl" alt="멘토 이미지" />
        </div>
        <div class="mentor-name">
          <div
            class="mentor-detail-view-left-mentor-name-text title-md white"
            :class="{ 'body-lg': isMobile }"
          >
            {{ mentor?.name }}
          </div>
        </div>
      </div>
      <div class="mentor-detail-view-right">
        <div class="mentor-profile">
          <div class="mentor-profile-title title-md white" :class="{ 'title-sm': isMobile }">
            멘토 약력
          </div>
          <div class="mentor-profile-content">
            <div class="mentor-profile-content-text body-sm white">
              {{ mentor?.description }}
            </div>
          </div>
        </div>
        <div class="mentor-program-content">
          <BaseHorizontalList
            v-if="!isMobile && videoList.length > 0"
            title="담당 프로그램 및 클래스"
            :showNavigation="true"
            :gap="20"
            :scrollWidth="433"
            :marginTop="0"
          >
            <BaseVideoThumbnail
              v-for="content in videoList"
              :key="content.idx"
              :lecture="content"
              :maxWidth="413"
              @click="handleVideoClick(content.idx)"
            />
          </BaseHorizontalList>
          <div v-else class="recommend-content-list-section">
            <div class="recommend-content-list-section-title title-sm white">
              담당 프로그램 및 클래스
            </div>
            <div class="recommend-content-list-section-list">
              <BaseVideoThumbnail
                class="recommend-content-list-section-list-item"
                v-for="content in videoList"
                :key="content.idx"
                :lecture="content"
                :showLevel="true"
                @click="handleVideoClick(content.idx)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseLayout>
</template>

<style scoped>
.mentor-detail-view {
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 20px;
}

.mentor-detail-view-left {
  flex: 413;
  position: relative;
}

.mentor-image {
  width: 413px;
  height: 642px;
  min-width: auto;
  min-height: auto;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 413 / 642;
  position: relative;
}

.mentor-image::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 66%, rgba(0, 0, 0, 0.8) 92.5%);
  pointer-events: none;
}

.mentor-name {
  position: absolute;
  bottom: 20px;
  left: 20px;
}

.mentor-detail-view-right {
  width: 100%;
  max-width: 847px;
  flex: 847;
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.mentor-profile {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mentor-profile-content {
  padding: 20px;
  background-color: var(--gray-800);
  border-radius: 8px;
  min-height: 260px;
  max-height: 260px;
  overflow-y: auto;
}

.mentor-profile-content-text {
  white-space: pre-line;
}

@media (max-width: 1365px) {
  .mentor-detail-view {
    flex-direction: column;
    padding: 16px 0px;
    gap: 32px;
  }

  .mentor-detail-view-left {
    flex: none;
    position: relative;
    padding: 0px 20px;
  }

  .mentor-image {
    width: 100%;
    min-width: auto;
    min-height: auto;
    border-radius: 8px;
    overflow: hidden;
    aspect-ratio: 335 / 521;
    position: relative;
  }

  .mentor-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .mentor-image::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 66%, rgba(0, 0, 0, 0.8) 92.5%);
    pointer-events: none;
  }

  .mentor-name {
    position: absolute;
    bottom: 20px;
    left: 32px;
  }

  .mentor-detail-view-right {
    width: 100%;
    max-width: 847px;
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  .mentor-profile {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0px 20px;
  }

  .mentor-profile-content {
    padding: 20px 12px;
    background-color: var(--gray-800);
    border-radius: 8px;
    min-height: 0px;
    max-height: none;
    overflow-y: auto;
  }

  .recommend-content-list-section-title {
    padding: 0px 20px;
    margin-bottom: 12px;
  }

  .recommend-content-list-section-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}
</style>
